import { Component, ViewChild } from '@angular/core';
import { environment } from '../../../environments/environment';
import { saveAs } from 'file-saver';

import { HttpClient } from '@angular/common/http';
import { ReportData } from '../../interfaces/reporte-response.interface';
import Swal from 'sweetalert2';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RegistroService } from '../../registro.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DateUtilsService } from '../../services/date-utils.service';
import {
    ModalPeriodoComponent,
    PeriodoSeleccionadoModal,
} from '../reporte-ventas-comercializadores/modal-periodo/modal-periodo.component';
import { ButtonComponent } from '../../shared/components/button/button.component';

type MonthOption = { name: string; number: number };
type QuarterOption = { name: string; number: number };
type SemesterOption = { name: string; number: number };

@Component({
    selector: 'app-reporte-filtro-periodo',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        ModalPeriodoComponent,
        ButtonComponent,
    ],
    templateUrl: './reporte-filtro-periodo.component.html',
    styleUrls: [
        '../../reporte-filtro-fecha/report-filter-date.component.css',
        './reporte-filtro-periodo.component.css',
    ],
})
export class ReporteFiltroPeriodoComponent {
    @ViewChild('modalPeriodoStart')
    modalPeriodoStart!: ModalPeriodoComponent;

    @ViewChild('modalPeriodoEnd')
    modalPeriodoEnd!: ModalPeriodoComponent;

    startDate = '';
    endDate = '';
    dataLoaded = false;
    reportData: ReportData[] = [];

    selectedYear: number | null = null;
    selectedMonth: MonthOption | null = null;
    selectedQuarter: QuarterOption | null = null;
    selectedSemester: SemesterOption | null = null;

    startPeriod: string = '';
    endPeriod: string = '';

    private readonly apiUrl: string = environment.apiSiriUrl;
    selectedStartPeriodYear: string = '';
    selectedEndPeriodYear: string = '';
    periodNamePlural: string = 'meses';
    periodNameSingular: string = 'mes';

    constructor(
        private readonly http: HttpClient,
        private readonly registroService: RegistroService,
        private readonly translate: TranslateService,
        private readonly dateUtils: DateUtilsService
    ) {
        this.translate.setDefaultLang('es');
        this.translate.use('es');
    }

    ngOnInit(): void {
        const periodosPorTamaño: Record<
            string,
            { singular: string; plural: string }
        > = {
            Grande: { singular: 'Mes', plural: 'meses' },
            Mediano: { singular: 'Trimestre', plural: 'trimestres' },
            Pequeño: { singular: 'Semestre', plural: 'semestres' },
        };

        const tamano =
            this.caracterizacionComercializador?.tamanoComercializador;
        const periodo = tamano ? periodosPorTamaño[tamano] : undefined;

        if (periodo) {
            this.periodNameSingular = periodo.singular;
            this.periodNamePlural = periodo.plural;
        }
    }

    get caracterizacionComercializador(): {
        tamanoComercializador?: string | null;
    } | null {
        return this.registroService.caracterizacionComercializador as {
            tamanoComercializador?: string | null;
        } | null;
    }

    toConsult() {
        this.http
            .get<
                ReportData[]
            >(`${this.apiUrl}reporte-comercializador/historico-periodo?fechaInicio=${this.startPeriod}&fechaFin=${this.endPeriod}&agenteVigiladoID=${this.registroService.empresaSelected}`)
            .subscribe({
                next: (response) => {
                    this.reportData = response.map((reporte) => ({
                        ...reporte,
                        periodoNombre: this.dateUtils.getPeriodName(
                            (reporte as any)?.periodo
                        ),
                    }));

                    this.dataLoaded = true;
                },
                error: (error) => {
                    console.error('Error', error);
                },
            });
    }

    exportData() {
        this.http
            .get(
                `${this.apiUrl}reporte-comercializador/exportar-cargue-historico?fechaInicio=${this.startPeriod}&fechaFin=${this.endPeriod}&agenteVigiladoID=${this.registroService.empresaSelected}`,
                { responseType: 'blob' }
            )
            .subscribe({
                next: (response: Blob) => {
                    saveAs(response, 'reporte.xlsx');
                },
                error: (error) => {
                    console.error('Error', error);
                },
            });
    }

    mostrarTexto() {
        this.translate
            .get('reporteHistoricos.alertaAyuda')
            .subscribe((translatedText: string) => {
                Swal.fire('', translatedText, 'info');
            });
    }

    openModalPeriodo(type: 'start' | 'end', mensaje: string): void {
        const modal =
            type === 'start' ? this.modalPeriodoStart : this.modalPeriodoEnd;
        modal.mensaje = mensaje;
        modal.type = type;
        if (type === 'end' && this.startDate) {
            modal.minDate = this.startDate;
        }
        modal.open();
    }

    obtenerNombrePeriodo(periodo: string | number | null | undefined): string {
        return this.dateUtils.getPeriodName(periodo);
    }

    onPeriodoSeleccionado(seleccion: PeriodoSeleccionadoModal): void {
        this.selectedYear = seleccion.year;
        this.selectedMonth = seleccion.month;
        this.selectedQuarter = seleccion.quarter;
        this.selectedSemester = seleccion.semester;
        const range = this.dateUtils.computeRange(
            this.caracterizacionComercializador?.tamanoComercializador,
            seleccion
        );
        if (seleccion.type === 'start') {
            this.selectedStartPeriodYear =
                this.selectedYear +
                '-' +
                (this.selectedMonth?.name ?? '') +
                (this.selectedQuarter?.name ?? '') +
                (this.selectedSemester?.name ?? '');
            this.startPeriod =
                this.selectedYear +
                (
                    (this.selectedMonth?.number ?? 0) +
                    (this.selectedQuarter?.number ?? 0) +
                    (this.selectedSemester?.number ?? 0)
                )
                    .toString()
                    .padStart(2, '0');
            this.startDate = range.start;
            this.syncEndIfStartAfterEnd();
        } else if (seleccion.type === 'end') {
            this.selectedEndPeriodYear =
                this.selectedYear +
                '-' +
                (this.selectedMonth?.name ?? '') +
                (this.selectedQuarter?.name ?? '') +
                (this.selectedSemester?.name ?? '');
            this.endDate = range.end;
            this.endPeriod =
                this.selectedYear +
                (
                    (this.selectedMonth?.number ?? 0) +
                    (this.selectedQuarter?.number ?? 0) +
                    (this.selectedSemester?.number ?? 0)
                )
                    .toString()
                    .padStart(2, '0');
        }
    }

    /**
     * Si ya había fin de periodo y la fecha inicio queda después, alinea fin con inicio
     * Comparación en ISO `yyyy-MM-dd` es segura lexicográficamente.
     */
    private syncEndIfStartAfterEnd(): void {
        if (!this.startDate?.trim()) {
            return;
        }
        if (!this.endDate?.trim()) {
            return;
        }
        if (this.startDate > this.endDate) {
            this.endDate = this.startDate;
            this.endPeriod = this.startPeriod;
            this.selectedEndPeriodYear = this.selectedStartPeriodYear;
        }
    }

    onStartDateChange(_event: Event): void {
        this.syncEndIfStartAfterEnd();
    }
}
